<script>
  import { onMount, createEventDispatcher } from 'svelte'

  import dayjs from 'dayjs'
  import relativeTime from 'dayjs/plugin/relativeTime'
  import updateLocale from 'dayjs/plugin/updateLocale'
  dayjs.extend(relativeTime)
  dayjs.extend(updateLocale)
  dayjs.updateLocale('en', {
    relativeTime: {
      future: "%s from now",
      past: "%s ago",
      s: 'a few seconds',
      m: "a minute",
      mm: "%d minutes",
      h: "an hour",
      hh: "%d hours",
      d: "a day",
      dd: "%d days",
      M: "a month",
      MM: "%d months",
      y: "a year",
      yy: "%d years"
    }
  })

  import { markdown } from '../misc.js'

  import authStore from '../auth/store.js'
  import approxNowStore from '../stores/approx-now.js'
  import userStore from '../stores/user.js'

  import toastStore from './toast/store.js'

  import MoreMenu from './widgets/more-menu.svelte'
  import AlertButton from './widgets/alert-button.svelte'
  import StarButton from './widgets/star-button.svelte'


  export let task

  const dispatch = createEventDispatcher()

  const alertTask = function (event) {
    const url = '/api/tasks/' + task.task_id
    const data = {
      'alert_dt': event.detail.alertDt,
    }
    return authStore.request({method: 'put', url, data})
      .then(() => {
        task.alert_dt = data.alert_dt
        dispatch('updated')
      })
      .catch(e => toastStore.error('Failed to set alert', e))
  }

  const unAlertTask = function () {
    const url = '/api/tasks/' + task.task_id
    const data = {
      'alert_dt': null,
    }
    return authStore.request({method: 'put', url, data})
      .then(() => {
        task.alert_dt = null
        dispatch('updated')
      })
      .catch(e => toastStore.error('Failed to remove alert', e))
  }

  const starTask = function () {
    const url = '/api/tasks/' + task.task_id
    const data = {
      'starred': true,
    }
    return authStore.request({method: 'put', url, data})
      .then(() => {
        task.starred = true
        dispatch('updated')
      })
      .catch(e => toastStore.error('Failed to star task', e))
  }

  const unStarTask = function () {
    const url = '/api/tasks/' + task.task_id
    const data = {
      'starred': false,
    }
    return authStore.request({method: 'put', url, data})
      .then(() => {
        task.starred = false
        dispatch('updated')
      })
      .catch(e => toastStore.error('Failed to un-star task', e))
  }

  const editTask = function () {
    dispatch('edit')
  }

  const deleteTask = function () {
    const taskId = task.task_id

    const url = '/api/tasks/' + taskId + '/delete?wait_for'
    return authStore.request({method: 'post', url})
      .then(() => toastStore.success('Deleted', () => restoreTask(taskId)))
      .then(() => {
        dispatch('updated', {reload: true})
      })
      .catch(e => toastStore.error('Failed to delete task', e))
  }

  const restoreTask = function (taskId) {
    const url = '/api/tasks/' + taskId + '/queue?wait_for'
    return authStore.request({method: 'post', url})
      .then(() => toastStore.success('Re-queued'))
      .then(() => {
        dispatch('updated', {reload: true})
      })
      .catch(e => toastStore.error('Failed to re-queue task', e))
  }

  const moreMenuItems = [
    {type: 'button', text: 'Edit', func: editTask},
    {type: 'button', text: 'Delete', func: deleteTask},
  ]

  onMount(() => {
    userStore.maybeInit()
  })
</script>

<div class="taskWrapper">
  <div class="taskHeader">
    <div class="taskInfo">
      Queued {dayjs(task.queue_dt).from($approxNowStore)}
      {#if task.status === 'queued'}
        {#if dayjs(task.active_dt) > $approxNowStore}
          <br>Deferred to {dayjs(task.active_dt).from($approxNowStore)}
        {/if}
        {#if $userStore && $userStore.alertsEnabled}
          {#if task.alert_dt && dayjs(task.alert_dt) > $approxNowStore}
            <br>Alerts {dayjs(task.alert_dt).from($approxNowStore)}
          {/if}
        {/if}
      {:else if task.status === 'completed'}
        <br>Completed {dayjs(task.complete_dt).from($approxNowStore)}
      {:else if task.status === 'deleted'}
        <br>Deleted {dayjs(task.delete_dt).from($approxNowStore)}
      {/if}
    </div>
    {#if task.status === 'queued'}
      <AlertButton alertDt={task.alert_dt}
                   on:alert={alertTask}
                   on:unAlert={unAlertTask} />
      <StarButton starred={task.starred}
                  on:star={starTask}
                  on:unStar={unStarTask} />
      <MoreMenu menuItems={moreMenuItems} position="belowLeft" />
    {/if}
  </div>
  <div class="task markdown">{@html markdown(task.body)}</div>
</div>

<style>
  .taskWrapper {
    border: 1px solid var(--border-color);
    border-radius: 5px;

    background-color: white;
  }

  .taskHeader {
    display: flex;

    border-bottom: 1px solid var(--border-color);
  }
  .taskHeader :global(.iconButton) {
    padding: 10px;
  }

  .taskInfo {
    flex-grow: 1;
    align-self: center;

    padding: 10px;

    font-size: 0.8em;
    font-weight: 400;
  }

  .task {
    padding: 10px;

    font-size: 1.1em;
  }
</style>